import React, { useMemo } from 'react';
import Select from 'components/global/CUK/AsyncSelect/Select';
import { components } from 'react-select';
import { useTranslation } from 'react-i18next';
import { Typography, useTheme, Box, ClickAwayListener } from '@mui/material';
import { OptionMultiCount } from 'components/global/CUK/AsyncSelect/components/Option';
import StateIcon from 'components/global/StateIcon';
import { useUser } from 'store';

const DropdownIndicator = ({ ...props }) => {
  const theme = useTheme()
  return (
    <Box mr={1}>
      {props.selectProps.menuIsOpen ? (
        <StateIcon
          iconlabel={'angle-up'}
          iconweight={'regular'}
          iconcolor={theme.palette.primary.main}
        />
      ) : (
        <StateIcon iconweight={'regular'} iconlabel={'angle-down'} />
      )}
    </Box>
  )
}

const ValueContainer = ({ ...props }) => {
  const theme = useTheme()
  const selectedValuesCount = props.getValue().length
  const { menuIsOpen } = props.selectProps

  return (
    <components.ValueContainer {...props}>
      <Box>
        <Typography
          sx={{
            color: menuIsOpen ? '#3B82F6' : theme.palette.grey[500],
            fontSize: '12px',
            paddingLeft: theme.spacing(1.5),
            fontWeight: 500,
          }}
        >
          {selectedValuesCount === 0
            ? props.selectProps.placeholder
            : `${selectedValuesCount} sélectionnés`}
        </Typography>
      </Box>
    </components.ValueContainer>
  )
}

const Option = (props) => {
  return (
    <OptionMultiCount
      {...props}
      value={props.data}
      count={props.data.count}
      availability={props.data.value}
    />
  )
}

const TypeSelect = ({ apiCount = {}, onChange, values = [], isCenterDocument }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [user] = useUser();

  const CENTER_DOCUMENT_TYPES = [
    { key: 'financial_report', label: t('documents.edition.types.financial_report'), count: '-', role: 'documents_financial_report' },
    { key: 'indicator_report', label: t('documents.edition.types.indicator_report'), count: '-', role: 'documents_indicator_report' },
    { key: 'support_document', label: t('documents.edition.types.support_material'), count: '-', role: 'documents_support_documentation' },
    { key: 'other', label: t('documents.edition.types.others'), count: '-', role: 'documents_other_documents' },
  ];

  const typeCounts = apiCount?.type || {};

  const hasRole = (role) => {
    if (!user || !user.roles) return false;
    if (Array.isArray(user.roles)) {
      return user.roles.includes(role);
    }
    if (typeof user.roles === 'object') {
      return !!user.roles[role];
    }
    return false;
  };

  const AUDIN_DOCUMENT_TYPES = [
    {
      label: 'audin_document.report_type.audit',
      value: 'audit',
      role: 'documents_audin_audit',
      id: 1
    },
    {
      label: 'audin_document.report_type.integration',
      value: 'integration',
      role: 'documents_audin_integration',
      id: 2
    },
    {
      label: 'audin_document.report_type.technical_visit',
      value: 'technicalvisit',
      role: 'documents_audin_technical_visit',
      id: 3
    },
    {
      label: 'audin_document.report_type.revised_technical_visit',
      value: 'revisedtechnicalvisit',
      role: 'documents_audin_revised_technical_visit',
      id: 4
    }
  ];

  const choices = useMemo(() => {
    if (isCenterDocument) {
      return CENTER_DOCUMENT_TYPES
        .filter(item => user.admin || hasRole(item.role))
        .map(item => ({
          label: item.label,
          value: item.key,
          count: typeCounts[item.key] ?? '-',
          id: item.key,
        }));
    }

    return AUDIN_DOCUMENT_TYPES
      .filter(item => user.admin || hasRole(item.role))
      .map(item => ({
        label: t(item.label),
        value: item.value,
        count: typeCounts[item.value] ?? 0,
        id: item.id
      }));
  }, [isCenterDocument, user.roles, typeCounts, t]);

  const handleOnChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    onChange(selectedValues);
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  return (
    <Select
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={choices}
      mainField="label"
      placeholder={t('audin_document.filter.types_placeholder')}
      onChange={handleOnChange}
      components={{
        Option,
        Control: ({ children, ...props }) => {
          const { menuIsOpen, onMenuClose, onMenuOpen } = props.selectProps;
          return (
            <components.Control {...props}>
              <ClickAwayListener
                mouseEvent='onMouseDown'
                onClickAway={() => {
                  onMenuClose();
                }}
              >
                <Box
                  onClick={() => {
                    if (menuIsOpen) {
                      onMenuClose();
                    } else {
                      onMenuOpen();
                    }
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexGrow: 1,
                    gap: 7,
                    cursor: 'pointer'
                  }}
                >
                  {children}
                </Box>
              </ClickAwayListener>
            </components.Control>
          );
        },
        MenuList: ({ children, ...props }) => {
          return (
            <components.MenuList {...props}>
              {children}
              <Box
                ml={1}
                mt={1}
                color={'grey.600'}
                fontSize={12}
                fontWeight={500}
                onClick={handleDeselectAll}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.grey[800],
                  },
                }}
              >
                {t('actions.deselect_all')}
              </Box>
            </components.MenuList>
          );
        },
        DropdownIndicator,
        ValueContainer,
        Placeholder: (props) => (
          <Box>
            <components.Placeholder {...props} />
          </Box>
        ),
        IndicatorSeparator: null,
        ClearIndicator: null,
      }}
      value={choices.filter((choice) => values.includes(choice.value))}
    />
  );
};

export default TypeSelect;